import { createSlice, current, PayloadAction } from '@reduxjs/toolkit'
import type {
  EmailListModelRead,
  EmailMessageQueryResultRead,
  EmailModelRead,
  EmailStatisticsModel,
  JunkEmailModel,
  JunkMailDestination,
  MessagesOperationsPatchModel,
  MoveJunkModel,
  ReadStatusSummary,
} from 'api/inbox2'
import {
  ParentRelationEnum,
  IEmailListItem,
  IEmailModel,
  ISendingStatusPayload,
} from 'Modules/Communication/Email/shared/models/Email'
import {
  IDatesQueryModel,
  IInboxFilters,
  InboxDeleteStatus,
  InboxSectionEnum,
  MessageState,
} from 'Modules/Communication/shared/models/Inbox'
import { ILabel } from 'Modules/Communication/shared/models/Labels'
import { ICommonListModel } from 'shared/models/Common'
import { SendEmailFieldName } from 'shared/models/Message'
import { merge } from 'shared/utils/mergeObjects'
import { EmailFiltersUpdate } from './types'

const emptyDates = (): IDatesQueryModel => ({
  received: { from: undefined, to: undefined },
  sent: { from: undefined, to: undefined },
  deleted: { from: undefined, to: undefined },
  processed: { from: undefined, to: undefined },
})

const asEmailList = (list: EmailMessageQueryResultRead) =>
  list as unknown as ICommonListModel<IEmailListItem>

const asEmailListItems = (items: EmailListModelRead[]) =>
  items as unknown as IEmailListItem[]

const asEmail = (email: EmailModelRead | JunkEmailModel) =>
  email as unknown as IEmailModel

const emptySeenBySummary = (): ReadStatusSummary => ({
  messageId: null,
  readCount: 0,
})

const initialFilters: IInboxFilters = {
  searchText: '',
  queryStatus: InboxSectionEnum.Received,
  labelId: '',
  take: 20,
  skip: 0,
  onlyUnread: false,
  dates: emptyDates(),
}

const initialState = {
  data: null as ICommonListModel<IEmailListItem> | null,
  error: null as unknown,
  loading: false,
  loadingMore: false,
  updated: new Date(),
  filters: initialFilters,

  email: {
    current: null as string | null,
    data: null as IEmailModel | null,
    seenBySummary: emptySeenBySummary(),

    error: null as unknown,
    loading: false,
    completed: false,
  },

  marked: [] as string[],
  singleListActionId: null as string | null,
  deleteStatus: null as InboxDeleteStatus | null,
  unreadCount: null as number | null,
  junkmail: {
    processing: null as JunkMailDestination | null,
    error: null as unknown,
  },
  statistics: {
    unreadCount: null as number | null,
    sendingFailureCount: null as number | null,
  },
}

const emailsSlice = createSlice({
  name: 'emails',
  initialState,
  reducers: {
    startFetchingEmails: (state) => {
      state.loading = true
      state.error = null
      state.data = null
    },
    receiveEmails: (
      state,
      action: PayloadAction<EmailMessageQueryResultRead>
    ) => {
      state.loading = false
      state.data = asEmailList(action.payload)
      state.error = null
      state.updated = new Date()
    },
    failFetchingEmails: (state, action: PayloadAction<unknown>) => {
      state.loading = false
      state.error = action.payload
    },
    updateEmailFilters: (state, action: PayloadAction<EmailFiltersUpdate>) => {
      const keys = Object.keys(action.payload)
      const shouldResetMarked = keys.some((item) =>
        ['labelId', 'queryStatus', 'searchText', 'onlyUnread'].includes(item)
      )

      state.filters = merge(current(state.filters), action.payload, {
        searchText: action.payload.searchText || '',
      })

      if (keys.includes('queryStatus')) state.filters.onlyUnread = false
      if (shouldResetMarked) state.marked = []
      state.filters.skip = 0
    },
    startFetchingMoreEmails: (state) => {
      state.loadingMore = true
      state.filters.skip += 20
    },
    receiveMoreEmails: (
      state,
      action: PayloadAction<EmailMessageQueryResultRead>
    ) => {
      const payload = asEmailList(action.payload)
      state.loadingMore = false
      state.updated = new Date()

      if (!state.data) {
        state.data = payload
        return
      }

      const { result, ...page } = payload
      Object.assign(state.data, page)
      state.data.result = Array.isArray(result)
        ? (state.data.result ?? []).concat(result)
        : result
    },
    failFetchingMoreEmails: (state, action: PayloadAction<unknown>) => {
      state.loadingMore = false
      state.error = action.payload
    },
    updateEmailSection: (state, action: PayloadAction<InboxSectionEnum>) => {
      state.filters.queryStatus = action.payload
      state.filters.onlyUnread = false
      state.filters.skip = 0
      state.filters.dates = emptyDates()
      state.marked = []
    },
    receiveNewEmails: (state, action: PayloadAction<EmailListModelRead[]>) => {
      if (!state.data || !Array.isArray(state.data.result)) return

      const existingIds = state.data.result.map((item) => item.id)
      const newEmails = asEmailListItems(action.payload).filter(
        (email) => !existingIds.includes(email.id)
      )

      if (newEmails.length === 0) return

      state.data.result.unshift(...newEmails)
      state.data.totalCount += newEmails.length
      state.data.resultCount += newEmails.length
    },
    toggleEmailMarked: (state, action: PayloadAction<string>) => {
      const id = action.payload

      if (state.marked.includes(id)) {
        state.marked = state.marked.filter((item) => item !== id)
        return
      }

      state.email.current = null
      state.email.data = null
      state.marked.push(id)
    },
    resetEmailsMarked: (state) => {
      state.marked = []
    },
    setCurrentEmail: (state, action: PayloadAction<{ id: string | null }>) => {
      state.email.current = action.payload.id
      state.email.completed = action.payload.id === null
      state.email.data = null
      state.email.error = null
      state.email.loading = true
    },
    resetCurrentEmail: (state) => {
      state.email.current = null
      state.email.completed = true
      state.email.data = null
      state.email.error = null
      state.email.loading = false
    },
    receiveCurrentEmail: (
      state,
      action: PayloadAction<EmailModelRead | JunkEmailModel>
    ) => {
      state.email.completed = true
      state.email.data = asEmail(action.payload)
      state.email.error = null
      state.email.loading = false
    },
    failFetchingCurrentEmail: (state, action: PayloadAction<unknown>) => {
      state.email.completed = false
      state.email.data = null
      state.email.error = action.payload ?? true
      state.email.loading = false
    },
    receiveRefreshedEmail: (state, action: PayloadAction<EmailModelRead>) => {
      state.email.data = asEmail(action.payload)
    },
    updateEmailsReadStatus: (
      state,
      action: PayloadAction<MessagesOperationsPatchModel['model']>
    ) => {
      const patches = action.payload
      const isMarkAsRead = patches[0]?.patchModel[0]?.value

      if (state.data) {
        state.data.result =
          state.data.result?.map((email) => {
            const patch = patches.find((item) => item.id === email.id)
            if (patch) email.isRead = patch.patchModel[0].value || false
            return email
          }) || []
      }

      const isCurrentEmail = patches.some(
        (item) => item.id === state.email.current
      )
      if (isCurrentEmail && state.email.data) {
        state.email.data.readDate = state.email.data.readDate
          ? undefined
          : new Date()
      }

      if (state.filters.queryStatus === InboxSectionEnum.Received)
        state.statistics.unreadCount = isMarkAsRead
          ? (state.statistics.unreadCount || 0) - patches.length
          : (state.statistics.unreadCount || 0) + patches.length
    },
    addEmailLabels: (state, action: PayloadAction<ILabel[]>) => {
      if (state.email.data) {
        action.payload.forEach((label) => {
          if (!state.email.data?.labels?.some((item) => item.id === label.id))
            state.email.data?.labels?.push(label)
        })
      }

      const listIds = state.email.current ? [state.email.current] : state.marked

      listIds?.forEach((id) => {
        const email = state.data?.result?.find((item) => item.id === id)

        if (email) {
          action.payload.forEach((newLabel) => {
            if (!email.labels?.some((item) => item.id === newLabel.id)) {
              email.labels?.push(newLabel)
            }
          })
        }
      })
    },
    removeEmailLabel: (state, action: PayloadAction<string>) => {
      const currentEmailId = state.email.current

      if (state.filters.queryStatus === InboxSectionEnum.All) {
        state.email.current = null
        state.email.data = null

        if (state.data?.result.length) {
          state.data.result = state.data.result.filter(
            (item) => item.id !== currentEmailId
          )
        }
      } else if (state.email.data) {
        state.email.data.labels = state.email.data.labels?.filter(
          (label) => label.id !== action.payload
        )
      }

      const listItem = state.data?.result?.find(
        (item) => item.id === currentEmailId
      )

      if (listItem)
        listItem.labels = listItem.labels.filter(
          (label) => label.id !== action.payload
        )
    },
    unsetEmailProcessed: (state, action: PayloadAction<string>) => {
      if (
        state.data?.result.length &&
        state.filters.queryStatus !== InboxSectionEnum.All
      ) {
        state.data.result = state.data.result.filter(
          (item) => item.id !== action.payload
        )
      }

      if (state.data && state.filters.queryStatus === InboxSectionEnum.All) {
        const email = state.email.data
        state.data.result.forEach((item) => {
          if (email && action.payload === item.id) {
            item.state = String(email.status) as MessageState
          }
        })
      }
    },
    setEmailsProcessed: (
      state,
      action: PayloadAction<{ messageIds: string[] }>
    ) => {
      const { messageIds } = action.payload

      if (
        state.filters.queryStatus !== InboxSectionEnum.Processed &&
        state.filters.queryStatus !== InboxSectionEnum.All &&
        state.data
      ) {
        state.data.result = state.data.result.filter(
          (item) => !messageIds.includes(item.id)
        )
        state.marked = []
      }

      if (state.data && state.filters.queryStatus === InboxSectionEnum.All) {
        state.data.result.forEach((item) => {
          if (messageIds.includes(item.id)) item.state = MessageState.Processed
        })
      }
    },
    setSingleListEmailActionId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.singleListActionId = action.payload
    },
    startDeletingEmails: (
      state,
      _action: PayloadAction<{ ids: string[]; softDelete: boolean }>
    ) => {
      state.deleteStatus = InboxDeleteStatus.isDeleting
    },
    finishDeletingEmails: (state, action: PayloadAction<string[]>) => {
      state.deleteStatus = InboxDeleteStatus.success
      state.marked = []

      if (!state.data) return

      const isSoftDeleteInLabels =
        state.filters.queryStatus === InboxSectionEnum.All &&
        state.email.data?.state !== MessageState.Deleted

      if (isSoftDeleteInLabels) {
        state.data.result.forEach((item) => {
          if (action.payload.includes(item.id))
            item.state = MessageState.Deleted
        })
        return
      }

      state.data.result = state.data.result.filter(
        (item) => !action.payload.includes(item.id)
      )
    },
    failDeletingEmails: (state) => {
      state.deleteStatus = InboxDeleteStatus.error
    },
    resetDeleteStatus: (state) => {
      state.deleteStatus = null
    },
    finishRestoringEmails: (state, action: PayloadAction<string[]>) => {
      state.deleteStatus = InboxDeleteStatus.success
      state.marked = []

      if (!state.data) return

      if (state.filters.queryStatus !== InboxSectionEnum.All) {
        state.data.result = state.data.result.filter(
          (item) => !action.payload.includes(item.id)
        )
        return
      }

      const email = state.email.data
      state.data.result.forEach((item) => {
        if (!action.payload.includes(item.id) || !email?.status) return

        item.state = email.processedDate
          ? MessageState.Processed
          : (String(email.status) as MessageState)
      })
    },
    removeEmailsFromList: (state, action: PayloadAction<string[]>) => {
      state.marked = []
      if (state.data) {
        state.data.result = state.data.result.filter(
          (item) => !action.payload.includes(item.id)
        )
      }
    },
    markEmailRepliedOrForwarded: (
      state,
      action: PayloadAction<ISendingStatusPayload>
    ) => {
      const relation = action.payload.Relation?.Relation
      const emailInList = state.data?.result?.find(
        (email) => email.id === action.payload.Relation?.MessageId
      )

      if (!emailInList) return

      if (relation === ParentRelationEnum.forward)
        emailInList.isForwarded = true
      else if (
        relation === ParentRelationEnum.reply ||
        relation === ParentRelationEnum.replyAll
      )
        emailInList.isRepliedOn = true
    },
    receiveEmailStatistics: (
      state,
      action: PayloadAction<EmailStatisticsModel>
    ) => {
      state.statistics.unreadCount = action.payload.unreadCount || 0
      state.statistics.sendingFailureCount =
        action.payload.sendingFailureCount || 0
    },
    setEmailDraftField: (
      state,
      action: PayloadAction<{
        field: SendEmailFieldName
        value: string
        id: string
      }>
    ) => {
      const { field, value, id } = action.payload
      const emailInList = state.data?.result?.find((item) => item.id === id)
      const isOpenEmail = state.email?.data?.id === id

      if (field === 'contentHtml' && isOpenEmail)
        state.email.data.contentHtml = value
      else if (field === 'title') {
        if (emailInList) emailInList.title = value
        if (isOpenEmail) state.email.data.title = value
      }
    },
    receiveEmailSeenBySummary: (
      state,
      action: PayloadAction<ReadStatusSummary>
    ) => {
      state.email.seenBySummary = action.payload
    },
    resetEmailSeenBySummary: (state) => {
      state.email.seenBySummary = emptySeenBySummary()
    },
    startMovingJunk: (state, action: PayloadAction<MoveJunkModel>) => {
      state.junkmail.error = null
      state.junkmail.processing = action.payload.destination
    },
    finishMovingJunk: (state) => {
      state.junkmail.error = null
      state.junkmail.processing = null

      state.email.data = null
      state.email.current = null

      state.marked = []
    },
    failMovingJunk: (state, action: PayloadAction<unknown>) => {
      state.junkmail.error = action.payload
      state.junkmail.processing = null
    },
    resetMovingJunk: (state) => {
      state.junkmail.error = null
      state.junkmail.processing = null
    },
  },
})

export const {
  startFetchingEmails,
  receiveEmails,
  failFetchingEmails,
  updateEmailFilters,
  startFetchingMoreEmails,
  receiveMoreEmails,
  failFetchingMoreEmails,
  updateEmailSection,
  receiveNewEmails,
  toggleEmailMarked,
  resetEmailsMarked,
  setCurrentEmail,
  resetCurrentEmail,
  receiveCurrentEmail,
  failFetchingCurrentEmail,
  receiveRefreshedEmail,
  updateEmailsReadStatus,
  addEmailLabels,
  removeEmailLabel,
  unsetEmailProcessed,
  setEmailsProcessed,
  setSingleListEmailActionId,
  startDeletingEmails,
  finishDeletingEmails,
  failDeletingEmails,
  resetDeleteStatus,
  finishRestoringEmails,
  removeEmailsFromList,
  markEmailRepliedOrForwarded,
  receiveEmailStatistics,
  setEmailDraftField,
  receiveEmailSeenBySummary,
  resetEmailSeenBySummary,
  startMovingJunk,
  finishMovingJunk,
  failMovingJunk,
  resetMovingJunk,
} = emailsSlice.actions

export default emailsSlice.reducer
